/**
 * Utility functions for managing virtual folders for shared templates
 */

export interface VirtualFolder {
  id: string;
  type: 'virtual-folder';
  name: string;
  templateCount: number;
  sourceTeam: {
    id: string;
    name: string;
  };
  templates: any[];
  isVirtualFolder: true;
  isVirtualSharedFolder: true;
}

/**
 * Creates virtual folders from templates grouped by their source team
 */
export const createVirtualFolders = (templates: any[]): VirtualFolder[] => {
  const sharedByTeam = new Map<string, any[]>();

  // Group templates by their source team
  templates.forEach((template) => {
    if (template.sharedFromTeam) {
      const key = `virtual-shared-${template.sharedFromTeam.id}`;
      if (!sharedByTeam.has(key)) {
        sharedByTeam.set(key, []);
      }
      sharedByTeam.get(key)!.push(template);
    }
  });

  // Create virtual folder entries
  return Array.from(sharedByTeam.entries()).map(([key, teamTemplates]) => ({
    id: key,
    type: 'virtual-folder' as const,
    name: `Shared from "${teamTemplates[0].sharedFromTeam.name}"`,
    templateCount: teamTemplates.length,
    sourceTeam: teamTemplates[0].sharedFromTeam,
    templates: teamTemplates,
    isVirtualFolder: true as const,
    isVirtualSharedFolder: true as const,
  }));
};

/**
 * Checks if an ID belongs to a virtual folder
 */
export const isVirtualFolderId = (id: string | null | undefined): boolean => {
  return id?.startsWith('virtual-shared-') ?? false;
};

/**
 * Extracts the team ID from a virtual folder ID
 */
export const getTeamIdFromVirtualFolder = (virtualId: string): string => {
  return virtualId.replace('virtual-shared-', '');
};

/**
 * Gets templates for a specific virtual folder
 */
export const getTemplatesForVirtualFolder = (templates: any[], virtualFolderId: string): any[] => {
  const teamId = getTeamIdFromVirtualFolder(virtualFolderId);
  return templates.filter((template) => template.sharedFromTeam?.id === teamId);
};

/**
 * Filters templates to exclude those that belong in virtual folders
 */
export const excludeSharedTemplates = (templates: any[]): any[] => {
  return templates.filter((template) => !template.sharedFromTeam);
};

/**
 * Creates a virtual folder item for display in tables/lists
 */
export const createVirtualFolderItem = (virtualFolder: VirtualFolder) => ({
  type: 'folder' as const,
  template: {
    id: virtualFolder.id,
    isFolder: true,
    isVirtualSharedFolder: true,
    sourceTeam: virtualFolder.sourceTeam,
    organizationName: virtualFolder.sourceTeam.name,
  },
  name: virtualFolder.name,
  status: 'active',
  updatedAt: new Date().toISOString(), // Virtual folders show current date
  templateType: 'Folder',
  templateCreator: virtualFolder.sourceTeam.name,
  templateCount: virtualFolder.templateCount,
  onRowClick: null, // Will be set by the component
});
